import { useId, useReducer } from "react";
import { DateTime } from "luxon";
import {
  QuotesIdSchema,
  QuoteTrackingStateSchema,
  quoteTrackingLabels,
  type QuoteTracking,
  type QuoteRealization,
} from "cadendar-shared";
import { useUpdateQuoteTracking, useRefreshQuotes } from "./useQuotes";
import { Code, ConnectError } from "@connectrpc/connect";
import { quoteTrackingDraftReducer } from "./quoteTrackingDraft";
import QuoteRealizationIndicator from "./QuoteRealizationIndicator";

export function formatQuoteDate(date: string | null) {
  return date ? DateTime.fromISO(date).toFormat("dd/MM/yyyy") : "Date inconnue";
}

export default function QuoteTrackingControl({
  quoteId,
  tracking,
  realization,
}: {
  quoteId: string;
  tracking: QuoteTracking;
  realization?: QuoteRealization;
}) {
  const id = useId();
  const refresh = useRefreshQuotes();
  const [draft, dispatch] = useReducer(quoteTrackingDraftReducer, null);
  const mutation = useUpdateQuoteTracking(() => dispatch({ type: "close" }));
  const today = DateTime.now().toFormat("yyyy-MM-dd");

  return (
    <div className="space-y-2 text-sm">
      <div className="flex flex-wrap items-center gap-2">
        <span
          className={`rounded px-2 py-1 font-medium ${
            tracking.state === "invalid"
              ? "bg-slate-200 text-slate-600"
              : tracking.state === "accepted"
                ? "bg-green-100 text-green-800"
                : "bg-blue-50 text-blue-900"
          }`}
        >
          {quoteTrackingLabels[tracking.state]}
        </span>
        {!draft && (
          <button
            type="button"
            className="text-blue-700 underline"
            onClick={() => {
              mutation.reset();
              dispatch({ type: "open", tracking, today });
            }}
          >
            Modifier le statut
          </button>
        )}
      </div>
      {realization && <QuoteRealizationIndicator realization={realization} />}
      {tracking.deliveryDate && (
        <p className="text-slate-600">
          Remis le {formatQuoteDate(tracking.deliveryDate)}
        </p>
      )}
      {tracking.acceptanceDate && (
        <p className="text-slate-600">
          Accepté le {formatQuoteDate(tracking.acceptanceDate)}
        </p>
      )}
      {draft && (
        <form
          className="min-w-64 space-y-3 rounded border border-slate-200 bg-white p-3"
          onSubmit={(event) => {
            event.preventDefault();
            mutation.mutate({
              quoteId: QuotesIdSchema.parse(quoteId),
              state: draft.state,
              effectiveDate: draft.effectiveDate || null,
              deliveryDate: draft.deliveryDate || null,
              acceptanceDate: draft.acceptanceDate || null,
              expectedHistoryLength: draft.expectedHistoryLength,
            });
          }}
        >
          <fieldset disabled={mutation.isPending} className="space-y-3">
            <div>
              <label htmlFor={`${id}-state`} className="block font-medium">
                Statut du devis
              </label>
              <select
                id={`${id}-state`}
                className="mt-1 w-full rounded border p-2"
                value={draft.state}
                onChange={(event) =>
                  dispatch({
                    type: "state",
                    value: QuoteTrackingStateSchema.parse(event.target.value),
                  })
                }
              >
                {QuoteTrackingStateSchema.options.map((state) => (
                  <option key={state} value={state}>
                    {quoteTrackingLabels[state]}
                  </option>
                ))}
              </select>
            </div>
            {(
              [
                { field: "effectiveDate", label: "Date du statut" },
                { field: "deliveryDate", label: "Date de remise" },
                { field: "acceptanceDate", label: "Date d’acceptation" },
              ] satisfies Array<{
                field: "effectiveDate" | "deliveryDate" | "acceptanceDate";
                label: string;
              }>
            ).map(({ field, label }) => (
              <div key={field}>
                <label htmlFor={`${id}-${field}`} className="block font-medium">
                  {label}
                </label>
                <input
                  id={`${id}-${field}`}
                  type="date"
                  max={today}
                  className="mt-1 w-full rounded border p-2"
                  value={draft[field]}
                  onChange={(event) =>
                    dispatch({ type: "date", field, value: event.target.value })
                  }
                />
              </div>
            ))}
            <p className="text-xs text-slate-500">
              Laissez les dates inconnues vides. Les dates proposées sont à
              vérifier. Sans date de remise, le devis est exclu du taux
              d’acceptation.
            </p>
            {draft.state === "invalid" && (
              <p className="text-amber-800">
                Devis erroné ou remplacé : exclu de toutes les statistiques.
              </p>
            )}
            {draft.state === "abandoned" && (
              <p className="text-slate-600">
                Une acceptation antérieure reste conservée dans l’historique et
                dans le taux.
              </p>
            )}
            <div className="flex flex-wrap gap-2">
              <button
                type="submit"
                className="rounded bg-blue-700 px-3 py-2 text-white disabled:opacity-50"
              >
                {mutation.isPending ? "Enregistrement…" : "Enregistrer"}
              </button>
              <button
                type="button"
                className="rounded border px-3 py-2"
                onClick={() => dispatch({ type: "close" })}
              >
                Annuler
              </button>
            </div>
          </fieldset>
          {mutation.error && (
            <div role="alert" className="space-y-2 text-red-700">
              <p>
                {mutation.error.message ||
                  "Impossible d’enregistrer le statut du devis."}
              </p>
              {mutation.error instanceof ConnectError &&
                mutation.error.code === Code.Aborted && (
                  <button
                    type="button"
                    className="underline"
                    onClick={async () => {
                      await refresh();
                      dispatch({ type: "close" });
                    }}
                  >
                    Recharger les données
                  </button>
                )}
            </div>
          )}
        </form>
      )}
      <details className="text-slate-600">
        <summary className="cursor-pointer">
          Historique ({tracking.history.length})
        </summary>
        {tracking.history.length === 0 ? (
          <p className="mt-2 text-xs">
            Aucun changement enregistré dans Cadendar. Les dates anciennes ne
            sont pas reconstituées.
          </p>
        ) : (
          <ol className="mt-2 space-y-3 border-l border-slate-200 pl-3">
            {[...tracking.history].reverse().map((entry, index) => (
              <li key={`${entry.recordedAt}-${index}`} className="text-xs">
                <p>
                  {quoteTrackingLabels[entry.previousState]} →{" "}
                  <strong>{quoteTrackingLabels[entry.state]}</strong> ·{" "}
                  {formatQuoteDate(entry.effectiveDate)}
                </p>
                <p>
                  Remise :{" "}
                  {entry.previousDeliveryDate !== entry.deliveryDate && (
                    <>{formatQuoteDate(entry.previousDeliveryDate)} → </>
                  )}
                  {formatQuoteDate(entry.deliveryDate)} · Acceptation :{" "}
                  {entry.previousAcceptanceDate !== entry.acceptanceDate && (
                    <>{formatQuoteDate(entry.previousAcceptanceDate)} → </>
                  )}
                  {formatQuoteDate(entry.acceptanceDate)}
                </p>
                <p className="text-slate-400">
                  Enregistré le{" "}
                  {DateTime.fromISO(entry.recordedAt).toFormat(
                    "dd/MM/yyyy à HH:mm",
                  )}
                </p>
              </li>
            ))}
          </ol>
        )}
      </details>
    </div>
  );
}
